import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  ArrowLeft,
  UploadSimple,
  FileXls,
  FileCsv,
  MapPin,
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  Warning,
  ArrowRight,
  Spinner,
  Trash,
  Users,
  Phone,
  Envelope,
  InstagramLogo,
  CalendarBlank,
  NoteBlank,
} from '@phosphor-icons/react';
import { API, formatPhone } from './constants';

const STEPS = ['Upload', 'Mapeamento', 'Preview', 'Resultado'];

const CAMPO_CONFIG = {
  nome: { label: 'Nome', icon: Users, required: true },
  telefone: { label: 'Telefone', icon: Phone },
  email: { label: 'Email', icon: Envelope },
  instagram: { label: 'Instagram', icon: InstagramLogo },
  aniversario: { label: 'Aniversario', icon: CalendarBlank },
  observacoes: { label: 'Observacoes', icon: NoteBlank },
  profissional_recorrente: { label: 'Profissional recorrente', icon: Users },
  status: { label: 'Status', icon: CheckCircle },
  ultima_visita: { label: 'Ultima visita', icon: CalendarBlank },
  total_visitas: { label: 'Total de visitas', icon: Users },
  ticket_medio: { label: 'Ticket medio', icon: NoteBlank },
  servicos_recorrentes: { label: 'Servicos recorrentes', icon: NoteBlank },
  produtos_comprados: { label: 'Produtos comprados', icon: NoteBlank },
};

const CAMPOS_PRINCIPAIS = ['nome', 'telefone', 'email', 'instagram', 'aniversario', 'observacoes'];
const CAMPOS_LEGADO = ['profissional_recorrente', 'status', 'ultima_visita', 'total_visitas', 'ticket_medio', 'servicos_recorrentes', 'produtos_comprados'];

// ==================== STEP 1: UPLOAD ====================
const StepUpload = ({ onUploadSuccess }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setError('Formato nao suportado. Use .xlsx ou .csv');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/clientes/importar/upload`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erro no upload');
      }
      const data = await res.json();
      onUploadSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  return (
    <div className="max-w-lg mx-auto">
      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
          dragging ? 'border-[#1B4332] bg-[#1B4332]/5' : 'border-[#D4D4D8] hover:border-[#1B4332]/40 hover:bg-[#F7F6F4]'
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Spinner size={32} className="animate-spin text-[#1B4332]" />
            <p className="text-sm font-medium text-[#18181B]">Processando arquivo...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-3 mb-4">
              <FileXls size={36} weight="duotone" className="text-[#2D6A4F]" />
              <FileCsv size={36} weight="duotone" className="text-[#52B788]" />
            </div>
            <p className="text-sm font-medium text-[#18181B] mb-1">
              Arraste sua planilha aqui
            </p>
            <p className="text-xs text-[#71717A] mb-4">
              ou clique para selecionar
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B4332] text-white text-xs font-medium rounded-xl">
              <UploadSimple size={14} weight="bold" />
              Selecionar arquivo
            </div>
            <p className="text-[10px] text-[#A1A1AA] mt-4">
              Formatos: .xlsx, .csv — Max: 10MB
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </div>
      {error && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="upload-error">
          <XCircle size={16} weight="fill" className="text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
      <div className="mt-6 p-4 bg-white border border-[#E4E4E7] rounded-xl">
        <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide mb-2">Dica</p>
        <p className="text-xs text-[#71717A] leading-relaxed">
          Exporte seus clientes de qualquer sistema como planilha.
          O SalonUp detecta automaticamente as colunas e sugere o mapeamento.
          Apenas o campo <strong>nome</strong> e obrigatorio.
        </p>
      </div>
    </div>
  );
};

// ==================== STEP 2: MAPPING ====================
const StepMapping = ({ uploadData, mapping, setMapping, onNext, onBack }) => {
  const { headers, sample_rows } = uploadData;

  const handleFieldChange = (field, headerIdx) => {
    setMapping(prev => {
      const next = { ...prev };
      if (headerIdx === '') {
        delete next[field];
      } else {
        next[field] = parseInt(headerIdx);
      }
      return next;
    });
  };

  const nomeIsMapped = mapping.nome !== undefined && mapping.nome !== '';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm font-medium text-[#18181B]">{uploadData.filename}</p>
          <p className="text-xs text-[#71717A]">{uploadData.total_rows} registros encontrados</p>
        </div>
        <button onClick={onBack} className="text-xs text-[#71717A] hover:text-[#18181B] underline">Trocar arquivo</button>
      </div>

      <div className="space-y-2 mb-6">
        <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Campos principais</p>
        {CAMPOS_PRINCIPAIS.map(field => {
          const cfg = CAMPO_CONFIG[field];
          const Icon = cfg.icon;
          return (
            <div key={field} className="flex items-center gap-3 p-3 bg-white border border-[#E4E4E7] rounded-xl">
              <Icon size={16} weight="regular" className="text-[#71717A] flex-shrink-0" />
              <span className="text-sm font-medium text-[#18181B] w-32 flex-shrink-0">
                {cfg.label}{cfg.required && <span className="text-red-500 ml-0.5">*</span>}
              </span>
              <select
                data-testid={`map-${field}`}
                value={mapping[field] ?? ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="flex-1 px-3 py-2 bg-[#F7F6F4] border border-[#E4E4E7] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              >
                <option value="">— Nao mapear —</option>
                {headers.map((h, i) => (
                  <option key={i} value={i}>{h}{sample_rows[0] && sample_rows[0][i] ? ` (ex: ${String(sample_rows[0][i]).slice(0, 30)})` : ''}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <div className="space-y-2 mb-6">
        <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Historico legado (opcional)</p>
        {CAMPOS_LEGADO.map(field => {
          const cfg = CAMPO_CONFIG[field];
          const Icon = cfg.icon;
          return (
            <div key={field} className="flex items-center gap-3 p-3 bg-white border border-[#E4E4E7] rounded-xl">
              <Icon size={16} weight="regular" className="text-[#A1A1AA] flex-shrink-0" />
              <span className="text-sm text-[#71717A] w-32 flex-shrink-0">{cfg.label}</span>
              <select
                data-testid={`map-${field}`}
                value={mapping[field] ?? ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="flex-1 px-3 py-2 bg-[#F7F6F4] border border-[#E4E4E7] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              >
                <option value="">— Nao mapear —</option>
                {headers.map((h, i) => (
                  <option key={i} value={i}>{h}{sample_rows[0] && sample_rows[0][i] ? ` (ex: ${String(sample_rows[0][i]).slice(0, 30)})` : ''}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <div className="flex gap-3">
        <button onClick={onBack} className="flex-1 px-4 py-3 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
          Voltar
        </button>
        <button
          data-testid="btn-preview"
          onClick={onNext}
          disabled={!nomeIsMapped}
          className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: '#1B4332' }}
        >
          Pre-visualizar <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 3: PREVIEW ====================
const StepPreview = ({ previewData, onImport, onBack, importing }) => {
  const { preview, errors, duplicates, total_rows } = previewData;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl text-center">
          <p className="text-2xl font-semibold text-[#1B4332]" style={{ fontFamily: 'Outfit' }}>{total_rows}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Registros</p>
        </div>
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl text-center">
          <p className="text-2xl font-semibold text-amber-600" style={{ fontFamily: 'Outfit' }}>{duplicates.length}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Possiveis duplicatas</p>
        </div>
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl text-center">
          <p className="text-2xl font-semibold text-red-500" style={{ fontFamily: 'Outfit' }}>{errors.length}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Erros</p>
        </div>
      </div>

      {/* Duplicates warning */}
      {duplicates.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={14} weight="fill" className="text-amber-500" />
            <p className="text-xs font-medium text-amber-800">Possiveis duplicatas (serao ignoradas)</p>
          </div>
          <div className="space-y-1">
            {duplicates.slice(0, 5).map((d, i) => (
              <p key={i} className="text-[11px] text-amber-700">Linha {d.row}: {d.nome} (mesmo {d.tipo})</p>
            ))}
            {duplicates.length > 5 && <p className="text-[11px] text-amber-600">...e mais {duplicates.length - 5}</p>}
          </div>
        </div>
      )}

      {/* Preview table */}
      <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden mb-6" data-testid="preview-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#F7F6F4]">
                <th className="text-left p-3 text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Nome</th>
                <th className="text-left p-3 text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Telefone</th>
                <th className="text-left p-3 text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Email</th>
                <th className="text-left p-3 text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Instagram</th>
                <th className="text-left p-3 text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Aniversario</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i} className="border-b border-[#E4E4E7] last:border-0" data-testid={`preview-row-${i}`}>
                  <td className="p-3 font-medium text-[#18181B]">{row.nome}</td>
                  <td className="p-3 text-[#71717A]">{row.telefone ? formatPhone(row.telefone) : '-'}</td>
                  <td className="p-3 text-[#71717A]">{row.email || '-'}</td>
                  <td className="p-3 text-[#71717A]">{row.instagram || '-'}</td>
                  <td className="p-3 text-[#71717A]">{row.aniversario || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview.length < total_rows && (
          <div className="p-3 bg-[#F7F6F4] text-center">
            <p className="text-[11px] text-[#A1A1AA]">Mostrando {preview.length} de {total_rows} registros</p>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={onBack} className="flex-1 px-4 py-3 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
          Voltar
        </button>
        <button
          data-testid="btn-importar"
          onClick={onImport}
          disabled={importing || preview.length === 0}
          className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: '#1B4332' }}
        >
          {importing ? (
            <><Spinner size={14} className="animate-spin" /> Importando...</>
          ) : (
            <><UploadSimple size={14} weight="bold" /> Importar {total_rows} clientes</>
          )}
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 4: RESULT ====================
const StepResult = ({ result, onFinish }) => {
  return (
    <div className="max-w-md mx-auto text-center">
      <div className="mb-6">
        <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#F0FDF4' }}>
          <CheckCircle size={40} weight="fill" className="text-[#1B4332]" />
        </div>
        <h3 className="text-xl font-semibold text-[#18181B] mb-1" style={{ fontFamily: 'Outfit' }}>
          Importacao concluida
        </h3>
        <p className="text-sm text-[#71717A]">Seus clientes foram importados com sucesso.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl">
          <p className="text-2xl font-semibold text-[#1B4332]" style={{ fontFamily: 'Outfit' }}>{result.imported}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Importados</p>
        </div>
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl">
          <p className="text-2xl font-semibold text-amber-600" style={{ fontFamily: 'Outfit' }}>{result.skipped}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Ignorados</p>
        </div>
        <div className="p-4 bg-white border border-[#E4E4E7] rounded-xl">
          <p className="text-2xl font-semibold text-red-500" style={{ fontFamily: 'Outfit' }}>{result.errors}</p>
          <p className="text-[10px] text-[#71717A] uppercase tracking-wide mt-1">Erros</p>
        </div>
      </div>

      <button
        data-testid="btn-ver-clientes"
        onClick={onFinish}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium text-white hover:shadow-lg transition-all"
        style={{ backgroundColor: '#1B4332' }}
      >
        <Users size={16} weight="bold" />
        Ver clientes
      </button>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export const ClientesImportar = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [uploadData, setUploadData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleUploadSuccess = (data) => {
    setUploadData(data);
    const autoMap = {};
    Object.entries(data.auto_mapping || {}).forEach(([field, idx]) => {
      autoMap[field] = idx;
    });
    setMapping(autoMap);
    setStep(1);
  };

  const handlePreview = useCallback(async () => {
    if (!uploadData) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/clientes/importar/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: uploadData.upload_id, mapping }),
      });
      const data = await res.json();
      setPreviewData(data);
      setStep(2);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [uploadData, mapping]);

  const handleImport = useCallback(async () => {
    if (!uploadData) return;
    setImporting(true);
    try {
      const res = await fetch(`${API}/clientes/importar/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: uploadData.upload_id, mapping, skip_duplicates: true }),
      });
      const data = await res.json();
      setResult(data);
      setStep(3);
    } catch { /* */ }
    finally { setImporting(false); }
  }, [uploadData, mapping]);

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="importar-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao/clientes')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Clientes</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao/clientes')}>Clientes</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Importar</span>
          </p>
        </div>

        {/* Title */}
        <section className="mb-8 animate-fade-in-up">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <UploadSimple size={28} weight="regular" style={{ color: '#1B4332' }} />
            </div>
            <div>
              <h1 className="salonup-heading-lg">Importar Clientes</h1>
              <p className="salonup-text-sm mt-1">Traga sua base de clientes de qualquer planilha</p>
            </div>
          </div>
        </section>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8" data-testid="step-indicator">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                i === step ? 'bg-[#1B4332] text-white' : i < step ? 'bg-[#F0FDF4] text-[#1B4332]' : 'bg-[#F4F4F5] text-[#A1A1AA]'
              }`}>
                {i < step ? <CheckCircle size={12} weight="fill" /> : null}
                <span>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-6 h-px ${i < step ? 'bg-[#1B4332]' : 'bg-[#D4D4D8]'}`} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="animate-fade-in-up">
          {step === 0 && <StepUpload onUploadSuccess={handleUploadSuccess} />}
          {step === 1 && (
            <StepMapping
              uploadData={uploadData}
              mapping={mapping}
              setMapping={setMapping}
              onNext={handlePreview}
              onBack={() => { setStep(0); setUploadData(null); setMapping({}); }}
            />
          )}
          {step === 2 && previewData && (
            <StepPreview
              previewData={previewData}
              onImport={handleImport}
              onBack={() => setStep(1)}
              importing={importing}
            />
          )}
          {step === 3 && result && (
            <StepResult
              result={result}
              onFinish={() => navigate('/gestao/clientes')}
            />
          )}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Spinner size={24} className="animate-spin text-[#1B4332]" />
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in mt-12">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
        </footer>
      </div>
    </div>
  );
};

export default ClientesImportar;
